import { useState, useEffect, ChangeEvent, useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useSessionStorage, useUnmount } from 'react-use';
import { isNil } from 'ramda';

import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IKnoOrganisationChild } from 'APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import { IProsecutorOrganisationChild } from 'APIPackage/CatalogsModule/interfaces/IProsecutorOrganisationChild';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TitleWithAddButton } from 'UIPackage/MainModule/components/TitleWithAddButton';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  getAccountKnoOrganizationFunctionsGroupData,
  getAccountOrganizationsData,
  getAccountOrganizationsStatus,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import { accountKnoOrganizationFunctionsGet } from 'CorePackage/AccountModule/actions/accountActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getUserAccountType,
  getUserProfileIsTest,
  getUserRoleIsProsecutor,
  getUserRoleIsProsecutorOrSuperProsecutorOrOmbudsman,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IS_ESIA } from 'CorePackage/MainModule/constants/localStorageNames';
import { UserAccountTypesEnum } from 'APIPackage/AuthModule/enums/UserAccountTypesEnum';
import { AccountStatusesEnum } from 'CorePackage/AccountModule/enums/AccountStatusesEnum';
import { IKnoOrganisationFunction } from 'APIPackage/AccountModule/interfaces/IKnoOrganisationFunction';

import styles from './UserInfo.module.scss';
import { PointLine } from './PointLine';
import { OrganisationsModal } from './OrganisationsModal';
import { RelatedOrganisationsModal } from './RelatedOrganisationsModal';
import { FunctionsModal } from './FunctionsModal';
import {
  userInfoProsecutorsByOrganizationsGet,
  userInfoReset,
  userInfoSetCurrentFunctionsModalId,
  userInfoSetDefaultFunction,
  userInfoUpdateOrganizations,
} from '../actions/userInfoActions';
import { mainProsecutorOrganizationDistrictId } from '../constants/mainProsecutorOrganizationDistrictId';
import {
  getUserInfoCurrentFunctionsModalId,
  getUserInfoProsecutorOrganisationByKno,
} from '../selectors/userInfoSelectors';
import { AddOrganisationsInputs } from './AddOrganisationsInputs';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { PROSECUTOR_ORGANIZATION_COUNT_MORE_ONE } from '../../../CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { isGeneralProsecutorOGRN } from '../../../CorePackage/MainModule/validators/isOgrn';

interface IUserInfoStoreProps {
  isGetSucceeded: boolean;
  isSupervisor: boolean;
  isProsecutorOrSuperProsecutorOrOmbudsman: boolean;
  userAccountType: UserAccountTypesEnum | undefined;
  knoOrganisations: IUserOrganisation[];
  prosecutorOrganisations: IUserOrganisation[];
  knoOrganisationFunctions: IKnoOrganisationFunction[];
  currentFunctionsModalId: string | undefined;
  prosecutorOrganisationByKno: IUserOrganisation[];
  isTestUser: boolean;
  isProsecutor: boolean;
}

const UserInfoWithoutStore = ({
  isGetSucceeded,
  isSupervisor,
  isProsecutorOrSuperProsecutorOrOmbudsman,
  userAccountType,
  knoOrganisations,
  currentFunctionsModalId,
  prosecutorOrganisations,
  knoOrganisationFunctions,
  prosecutorOrganisationByKno,
  isTestUser,
  isProsecutor,
}: IUserInfoStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isEsia] = useSessionStorage<boolean>(IS_ESIA);

  // В таблице МЗППО access_manager.user_account_type тип COMMON соответствует пользователям из Есиа
  const isEsiaAccount = userAccountType === UserAccountTypesEnum.COMMON;

  const [currentKnoOrganisations, setCurrentKnoOrganisations] = useState<Array<IUserOrganisation>>([]);
  const [currentProsecutorOrganisations, setCurrentProsecutorOrganisations] = useState<Array<IUserOrganisation>>([]);
  const [isOrganisationsModalVisible, setIsOrganisationsModalVisible] = useState(false);
  const [isRelatedOrganisationsModalVisible, setIsRelatedOrganisationsModalVisible] = useState(false);
  const [isFunctionsModalVisible, setIsFunctionsModalVisible] = useState(false);

  const isDevMode = isProsecutorOrSuperProsecutorOrOmbudsman && userAccountType === UserAccountTypesEnum.INTERNAL;
  const isUserProsecutorOrganizationsExist = prosecutorOrganisations.length && prosecutorOrganisations.length > 0;
  const isProsecutorOrganizationsEditEnabled =
    !isProsecutorOrSuperProsecutorOrOmbudsman ||
    isDevMode ||
    (isGetSucceeded && !isUserProsecutorOrganizationsExist) ||
    (isProsecutorOrSuperProsecutorOrOmbudsman &&
      prosecutorOrganisations.find(item => item.districtId === mainProsecutorOrganizationDistrictId)) ||
    (isEsiaAccount && isProsecutor);
  const isKnoOrganizationsEditEnabled = !isEsia && !(isSupervisor && userAccountType !== UserAccountTypesEnum.INTERNAL);

  const isExistLinkWithGeneralProsecutor = currentProsecutorOrganisations.some(item =>
    isGeneralProsecutorOGRN(item.ogrn),
  );

  useUnmount(() => {
    dispatch(userInfoReset());
  });

  useEffect(() => {
    setCurrentKnoOrganisations(knoOrganisations);
  }, [knoOrganisations]);

  useEffect(() => {
    if (!isTestUser) {
      if (currentKnoOrganisations.length === 0 && !isProsecutorOrSuperProsecutorOrOmbudsman) {
        setCurrentProsecutorOrganisations([]);
      } else {
        setCurrentProsecutorOrganisations(prosecutorOrganisations);
      }
    } else {
      setCurrentProsecutorOrganisations(prosecutorOrganisations);
    }
  }, [prosecutorOrganisations, currentKnoOrganisations, isTestUser]);

  const handleSelectedOrganisationsCheckboxChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;

    setCurrentKnoOrganisations(prev =>
      prev.map(organisation => ({ ...organisation, byDefault: organisation.id === id })),
    );
  }, []);

  const handleRelatedOrganisationsCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;

    setCurrentProsecutorOrganisations(prev =>
      prev.map(organisation => ({ ...organisation, byDefault: organisation.id === id })),
    );
  };

  const makeKnoOrganisationDelete = (id: string) => () => {
    const isActive = currentKnoOrganisations.find(organisation => organisation.id === id)?.byDefault;

    setCurrentKnoOrganisations(prev => {
      const newSelectedOrganisations = prev.filter(organisation => organisation.id !== id);

      if (newSelectedOrganisations.length && isActive) {
        newSelectedOrganisations[0].byDefault = true;
      }

      return newSelectedOrganisations;
    });
  };

  const makeProsecutorOrganisationDelete = (id: string) => () => {
    const isActive = currentProsecutorOrganisations.find(organisation => organisation.id === id)?.byDefault;

    setCurrentProsecutorOrganisations(prev => {
      const newRelatedOrganisations = prev.filter(organisation => organisation.id !== id);

      if (newRelatedOrganisations.length && isActive) {
        newRelatedOrganisations[0].byDefault = true;
      }

      return newRelatedOrganisations;
    });
  };

  const handleSaveButtonClick = useCallback(() => {
    const currentKnoOrganisationsErvkIds: string[] = currentKnoOrganisations
      .map(item => item.ervkVersionUuid)
      .filter((it): it is string => !isNil(it));
    const currentCheckedKnoOrganisationErvkId = currentKnoOrganisations.find(item => item.byDefault)?.ervkVersionUuid;
    const currentProsecutorOrganisationsIds = currentProsecutorOrganisations.map(item => item.id);
    const currentCheckedProsecutorOrganisationId = currentProsecutorOrganisations.find(item => item.byDefault)?.id;

    if (
      currentProsecutorOrganisationsIds?.length > 1 &&
      isProsecutorOrSuperProsecutorOrOmbudsman &&
      !isExistLinkWithGeneralProsecutor &&
      !(isEsiaAccount && isProsecutor)
    ) {
      dispatch(
        addNoticeAction({
          renderContent: PROSECUTOR_ORGANIZATION_COUNT_MORE_ONE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.LK_ACCOUNT_CHANGE,
        }),
      );
    } else {
      dispatch(
        userInfoUpdateOrganizations(
          currentKnoOrganisationsErvkIds,
          currentProsecutorOrganisationsIds,
          currentCheckedKnoOrganisationErvkId,
          currentCheckedProsecutorOrganisationId,
        ),
      );
    }
  }, [currentKnoOrganisations, currentProsecutorOrganisations, dispatch]);

  const openOrganisationsModal = useCallback(() => setIsOrganisationsModalVisible(true), []);

  const handleOrganisationsModalApply = useCallback((organisations: IKnoOrganisationChild[]) => {
    setIsOrganisationsModalVisible(false);
    setCurrentKnoOrganisations(prev => {
      const prevDefaultCurrentKnoOrganisationId = prev.find(item => item.byDefault)?.id;
      const defaultCurrentKnoOrganisationId =
        organisations.find(organisation => organisation.id === prevDefaultCurrentKnoOrganisationId)?.id ||
        organisations[0]?.id;

      return organisations.map(organisation => ({
        ...organisation,
        byDefault: organisation.id === defaultCurrentKnoOrganisationId,
        ervkVersionUuid: organisation.dictVersionId ?? organisation.ervkVersionUuid,
        districtName: null,
        districtId: null,
        saved: organisation.saved,
        isEnable: organisation.isEnable || prev.find(item => item.id === organisation.id)?.isEnable,
      }));
    });
  }, []);

  const knoIds = useMemo(
    () =>
      currentKnoOrganisations
        .map(item => ({
          id: item.ervkUuid != null ? item.ervkUuid : '',
        }))
        .filter(kno => kno.id.length > 0),
    [currentKnoOrganisations],
  );

  useEffect(() => {
    if (!isTestUser && !isProsecutorOrSuperProsecutorOrOmbudsman) {
      if (knoIds.length > 0) {
        dispatch(userInfoProsecutorsByOrganizationsGet(knoIds));
      }
    }
  }, [knoIds?.length]);

  const handleOrganisationsModalClose = useCallback(() => {
    setIsOrganisationsModalVisible(false);
  }, []);

  const openRelatedOrganisationsModal = useCallback(() => {
    setIsRelatedOrganisationsModalVisible(true);
  }, []);

  const closeRelatedOrganisationsModal = useCallback(() => setIsRelatedOrganisationsModalVisible(false), []);

  const handleRelatedOrganisationsModalApply = useCallback(
    (organisations: IProsecutorOrganisationChild[]) => {
      closeRelatedOrganisationsModal();
      setCurrentProsecutorOrganisations(prev => {
        const prevDefaultCurrentProsecutorOrganisationId = prev.find(item => item.byDefault)?.id;
        const defaultCurrentProsecutorOrganisationId =
          organisations.find(organisation => organisation.id === prevDefaultCurrentProsecutorOrganisationId)?.id ||
          organisations[0]?.id;

        return organisations.map(organisation => ({
          byDefault: organisation.id === defaultCurrentProsecutorOrganisationId,
          fullName: organisation.fullName,
          id: organisation.id,
          districtName: organisation.districtName,
          districtId: organisation.districtId,
          isEnable: organisation.isEnable || prev.find(item => item.id === organisation.id)?.isEnable,
          ogrn: organisation.ogrn,
          isLockedByTerritory: organisation.isLockedByTerritory,
        }));
      });
    },
    [closeRelatedOrganisationsModal],
  );

  useEffect(() => {
    setCurrentProsecutorOrganisations(prev => {
      const prevDefaultCurrentProsecutorOrganisationId = prev.find(item => item.byDefault)?.id;
      const defaultCurrentProsecutorOrganisationId =
        prosecutorOrganisationByKno.find(organisation => organisation.id === prevDefaultCurrentProsecutorOrganisationId)
          ?.id || prosecutorOrganisationByKno[0]?.id;

      return prosecutorOrganisationByKno.map(organisation => ({
        byDefault: organisation.id === defaultCurrentProsecutorOrganisationId,
        fullName: organisation.fullName,
        id: organisation.id,
        districtName: organisation.districtName,
        districtId: organisation.districtId,
        isEnable: organisation.isEnable || prev.find(item => item.id === organisation.id)?.isEnable,
        ogrn: organisation.ogrn,
        isLockedByTerritory: organisation.isLockedByTerritory,
      }));
    });
  }, [prosecutorOrganisationByKno]);

  const isRelatedOrganisationsModalAutoOpenActive =
    isProsecutorOrSuperProsecutorOrOmbudsman && isGetSucceeded && !isUserProsecutorOrganizationsExist;

  useEffect(() => {
    if (isRelatedOrganisationsModalAutoOpenActive) {
      setIsRelatedOrganisationsModalVisible(true);
    }
  }, [isRelatedOrganisationsModalAutoOpenActive]);

  const handleFunctionsButtonClick = (knoId: string) => () => {
    dispatch(userInfoSetCurrentFunctionsModalId(knoId));
    dispatch(accountKnoOrganizationFunctionsGet({ knoOrganizationId: knoId, isCaching: false }));
    setIsFunctionsModalVisible(true);
  };

  useUnmount(() => {
    dispatch(userInfoSetCurrentFunctionsModalId(undefined));
  });

  const handleFunctionsModalApply = useCallback(
    (knoId: string, functionId?: number) => {
      setIsFunctionsModalVisible(false);
      if (functionId) {
        dispatch(userInfoSetDefaultFunction(knoId, functionId));
      }
    },
    [dispatch],
  );

  const handleFunctionsModalClose = useCallback(() => setIsFunctionsModalVisible(false), []);

  return (
    <div className={styles.Wrapper}>
      <div className="shared-section-title">
        {isKnoOrganizationsEditEnabled && !(isEsiaAccount && isProsecutor) ? (
          <TitleWithAddButton text="Организация" onClick={openOrganisationsModal} />
        ) : (
          'Организация'
        )}
      </div>
      <div className={styles.SectionWrapper}>
        {currentKnoOrganisations.map(organisation => (
          <div key={organisation.id} className={styles.Point}>
            <PointLine
              id={organisation.id}
              name={organisation.fullName}
              groupName="kno"
              withFunctionsButton={organisation.saved}
              isActive={organisation.byDefault}
              onCheckboxChange={handleSelectedOrganisationsCheckboxChange}
              isCheckboxDisable={!(isSupervisor || isKnoOrganizationsEditEnabled)}
              onFunctionsButtonClick={handleFunctionsButtonClick(organisation.id)}
              onDelete={makeKnoOrganisationDelete(organisation.id)}
              isEnable={organisation.isEnable}
              isEditDisable={!isKnoOrganizationsEditEnabled}
              isWatchOnly={isEsiaAccount && isProsecutor}
            />
          </div>
        ))}
      </div>
      <div className="shared-section-title">
        {isTestUser ||
        (isProsecutorOrSuperProsecutorOrOmbudsman &&
          (!isUserProsecutorOrganizationsExist || isExistLinkWithGeneralProsecutor || isEsiaAccount)) ? (
          <TitleWithAddButton text="Связанные организации" onClick={openRelatedOrganisationsModal} />
        ) : (
          'Связанные организации'
        )}
      </div>
      {!isTestUser &&
        !isProsecutorOrSuperProsecutorOrOmbudsman &&
        currentKnoOrganisations.length > 0 &&
        currentProsecutorOrganisations.length === 0 && (
          <p className={styles.Warning}>
            Контрольный (надзорный) орган не связан с органом прокуратуры.
            <br />
            Внесите информацию в ЕРВК.
          </p>
        )}

      <div className={styles.SectionWrapper}>
        {currentProsecutorOrganisations.map(organisation => (
          <div key={organisation.id} className={styles.Point}>
            <PointLine
              id={organisation.id}
              name={organisation.fullName}
              groupName="related"
              isActive={organisation.byDefault && !organisation.isLockedByTerritory}
              isEnable={organisation.isEnable}
              isWatchOnly={!isTestUser && (isSupervisor || currentProsecutorOrganisations.length === 1)}
              onCheckboxChange={
                isProsecutorOrganizationsEditEnabled ? handleRelatedOrganisationsCheckboxChange : undefined
              }
              isCheckboxDisable={!isProsecutorOrganizationsEditEnabled || organisation.isLockedByTerritory}
              isEditDisable={
                isTestUser
                  ? false
                  : !isProsecutorOrSuperProsecutorOrOmbudsman && currentProsecutorOrganisations.length === 1
              }
              isLockedByTerritory={organisation.isLockedByTerritory}
              onDelete={makeProsecutorOrganisationDelete(organisation.id)}
            />
          </div>
        ))}
      </div>
      <Button
        className={styles.SaveButton}
        variant="contained"
        color="primary"
        text="Сохранить"
        id="saveButton"
        onClick={handleSaveButtonClick}
      />
      <OrganisationsModal
        visible={isOrganisationsModalVisible}
        selectedOrganisations={currentKnoOrganisations}
        onApply={handleOrganisationsModalApply}
        onClose={handleOrganisationsModalClose}
      >
        <AddOrganisationsInputs />
      </OrganisationsModal>
      <RelatedOrganisationsModal
        visible={isRelatedOrganisationsModalVisible}
        selectedOrganisations={currentProsecutorOrganisations}
        onApply={handleRelatedOrganisationsModalApply}
        onClose={closeRelatedOrganisationsModal}
        isEsiaAccount={isEsiaAccount}
      />
      <FunctionsModal
        visible={isFunctionsModalVisible}
        id={currentFunctionsModalId}
        functionsList={knoOrganisationFunctions}
        onApply={handleFunctionsModalApply}
        onClose={handleFunctionsModalClose}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IUserInfoStoreProps => {
  const currentFunctionsModalId = getUserInfoCurrentFunctionsModalId(state);

  return {
    isGetSucceeded: getAccountOrganizationsStatus(state) === AccountStatusesEnum.SUCCEEDED,
    isProsecutorOrSuperProsecutorOrOmbudsman: getUserRoleIsProsecutorOrSuperProsecutorOrOmbudsman(state),
    isSupervisor: getUserRoleIsSupervisor(state),
    isProsecutor: getUserRoleIsProsecutor(state),
    userAccountType: getUserAccountType(state),
    knoOrganisations: getAccountOrganizationsData(state).kno,
    prosecutorOrganisations: getAccountOrganizationsData(state).prosecutor,
    currentFunctionsModalId,
    knoOrganisationFunctions: currentFunctionsModalId
      ? getAccountKnoOrganizationFunctionsGroupData(state, currentFunctionsModalId)
      : [],
    prosecutorOrganisationByKno: getUserInfoProsecutorOrganisationByKno(state),
    isTestUser: getUserProfileIsTest(state),
  };
};

export const UserInfo = connect(mapStateToProps)(UserInfoWithoutStore);
